import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApplicationEntryPointActionModel} from '../../../models/api/applicationEntryPointActionModel';
import {ApplicationEntryPointTypesEnum} from '../../../models/api/applicationEntryPointModel';
import {
  ApiLocalApplicationChannelType,
  LocalApplicationChannelTypeModel,
  LocalApplicationChannelTypeSettingsModel,
} from '../../../models/api/localApplicationChannelTypeModel';
import {
  ApiLocalApplicationEntryPoint,
  ApiLocalApplicationEntryPoints,
  LocalApplicationEntryPointModel,
} from '../../../models/api/localApplicationEntryPointModel';
import {
  ApiLocalApplication,
  ApiLocalApplicationSigningSecret,
  ApiLocalApplicationWithCreator,
  LocalApplicationModel,
  LocalApplicationWithCreatorModel,
} from '../../../models/api/localApplicationModel';
import {
  ApiLocalApplicationServer,
  ApiLocalApplicationServers,
  LocalApplicationServerAuthenticationConfigModel,
  LocalApplicationServerModel,
  serializeLocalApplicationServerAuthenticationConfig,
} from '../../../models/api/localApplicationServerModel';
import {ApiOauthClient, ApiOauthClientSecret, OauthClientModel} from '../../../models/api/oauthClientModel';
import {
  ApiSearchLocalApplications,
  SearchLocalApplicationsModel,
} from '../../../models/api/searchLocalApplicationModel';
import {deleteAsync, fetchApiAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompanyUrl} from '../company/companySelectors';

/*
 * Action enum.
 */

export enum LocalApplicationActionsEnum {
  LOCAL_APPLICATION_DELETED = '@@data/localApplications/LOCAL_APPLICATION_DELETED',

  FETCH_LOCAL_APPLICATION_SERVERS = '@@data/localApplications/FETCH_LOCAL_APPLICATION_SERVERS',
  FETCH_LOCAL_APPLICATION_SERVERS_COMPLETED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_SERVERS_COMPLETED',
  FETCH_LOCAL_APPLICATION_SERVERS_FAILED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_SERVERS_FAILED',
  LOCAL_APPLICATION_SERVER_CREATED = '@@data/localApplications/LOCAL_APPLICATION_SERVER_CREATED',
  LOCAL_APPLICATION_SERVER_UPDATED = '@@data/localApplications/LOCAL_APPLICATION_SERVER_UPDATED',

  FETCH_LOCAL_APPLICATION_CHANNEL_TYPE = '@@data/localApplications/FETCH_LOCAL_APPLICATION_CHANNEL_TYPE',
  FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_COMPLETED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_COMPLETED',
  FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_FAILED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_FAILED',
  LOCAL_APPLICATION_CHANNEL_TYPE_CREATED = '@@data/localApplications/LOCAL_APPLICATION_CHANNEL_TYPE_CREATED',
  LOCAL_APPLICATION_CHANNEL_TYPE_DELETED = '@@data/localApplications/LOCAL_APPLICATION_CHANNEL_TYPE_DELETED',

  FETCH_LOCAL_APPLICATION_ENTRY_POINTS = '@@data/localApplications/FETCH_LOCAL_APPLICATION_ENTRY_POINTS',
  FETCH_LOCAL_APPLICATION_ENTRY_POINTS_COMPLETED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_ENTRY_POINTS_COMPLETED',
  FETCH_LOCAL_APPLICATION_ENTRY_POINTS_FAILED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_ENTRY_POINTS_FAILED',
  LOCAL_APPLICATION_ENTRY_POINT_CREATED = '@@data/localApplications/LOCAL_APPLICATION_ENTRY_POINT_CREATED',
  LOCAL_APPLICATION_ENTRY_POINT_DELETED = '@@data/localApplications/LOCAL_APPLICATION_ENTRY_POINT_DELETED',

  FETCH_LOCAL_APPLICATION_OAUTH_CLIENT = '@@data/localApplications/FETCH_LOCAL_APPLICATION_OAUTH_CLIENT',
  FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_COMPLETED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_COMPLETED',
  FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_FAILED = '@@data/localApplications/FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_FAILED',
}

/*
 * Action Types.
 */

interface LocalApplicationBasePayload {
  localApplicationId: number;
}

export interface LocalApplicationDeleteAction extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.LOCAL_APPLICATION_DELETED;
}

// Servers.

export interface FetchLocalApplicationServersAction extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS;
}

interface FetchLocalApplicationServersCompletedPayload extends LocalApplicationBasePayload {
  servers: ReadonlyArray<LocalApplicationServerModel>;
}

export interface FetchLocalApplicationServersCompletedAction
  extends ActionWithPayload<FetchLocalApplicationServersCompletedPayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS_COMPLETED;
}

export interface FetchLocalApplicationServersFailedAction
  extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS_FAILED;
}

interface LocalApplicationServerCreatedPayload extends LocalApplicationBasePayload {
  server: LocalApplicationServerModel;
}

export interface LocalApplicationServerCreatedAction
  extends ActionWithPayload<LocalApplicationServerCreatedPayload> {
  type: LocalApplicationActionsEnum.LOCAL_APPLICATION_SERVER_CREATED;
}

interface LocalApplicationServerUpdatedPayload extends LocalApplicationBasePayload {
  server: LocalApplicationServerModel;
}

export interface LocalApplicationServerUpdatedAction
  extends ActionWithPayload<LocalApplicationServerUpdatedPayload> {
  type: LocalApplicationActionsEnum.LOCAL_APPLICATION_SERVER_UPDATED;
}

// Channel Type.

export interface FetchLocalApplicationChannelTypeAction
  extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE;
}

interface FetchLocalApplicationChannelTypeCompletedPayload extends LocalApplicationBasePayload {
  channelType: LocalApplicationChannelTypeModel;
}

export interface FetchLocalApplicationChannelTypeCompletedAction
  extends ActionWithPayload<FetchLocalApplicationChannelTypeCompletedPayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_COMPLETED;
}

export interface FetchLocalApplicationChannelTypeFailedAction
  extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_FAILED;
}

interface LocalApplicationChannelTypeCreatedPayload extends LocalApplicationBasePayload {
  channelType: LocalApplicationChannelTypeModel;
}

export interface LocalApplicationChannelTypeCreatedAction
  extends ActionWithPayload<LocalApplicationChannelTypeCreatedPayload> {
  type: LocalApplicationActionsEnum.LOCAL_APPLICATION_CHANNEL_TYPE_CREATED;
}

export interface LocalApplicationChannelTypeDeletedAction
  extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.LOCAL_APPLICATION_CHANNEL_TYPE_DELETED;
}

// Entry Points.

export interface FetchLocalApplicationEntryPointsAction
  extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS;
}

interface FetchLocalApplicationEntryPointsCompletedPayload extends LocalApplicationBasePayload {
  entryPoints: ReadonlyArray<LocalApplicationEntryPointModel>;
}

export interface FetchLocalApplicationEntryPointsCompletedAction
  extends ActionWithPayload<FetchLocalApplicationEntryPointsCompletedPayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS_COMPLETED;
}

export interface FetchLocalApplicationEntryPointsFailedAction
  extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS_FAILED;
}

interface LocalApplicationEntryPointCreatedPayload extends LocalApplicationBasePayload {
  entryPoint: LocalApplicationEntryPointModel;
}

export interface LocalApplicationEntryPointCreatedAction
  extends ActionWithPayload<LocalApplicationEntryPointCreatedPayload> {
  type: LocalApplicationActionsEnum.LOCAL_APPLICATION_ENTRY_POINT_CREATED;
}

interface LocalApplicationEntryPointDeletedPayload extends LocalApplicationBasePayload {
  entryPointId: number;
}
export interface LocalApplicationEntryPointDeletedAction
  extends ActionWithPayload<LocalApplicationEntryPointDeletedPayload> {
  type: LocalApplicationActionsEnum.LOCAL_APPLICATION_ENTRY_POINT_DELETED;
}

interface FetchLocalApplicationOAuthClientAction extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT;
}

interface FetchLocalApplicationOAuthClientCompletedPayload extends LocalApplicationBasePayload {
  oAuthClient: OauthClientModel;
}

interface FetchLocalApplicationOAuthClientCompletedAction
  extends ActionWithPayload<FetchLocalApplicationOAuthClientCompletedPayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_COMPLETED;
}

interface FetchLocalApplicationOAuthClientFailedAction
  extends ActionWithPayload<LocalApplicationBasePayload> {
  type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_FAILED;
}

/*
 * Union Action type.
 */

export type LocalApplicationAction =
  | LocalApplicationDeleteAction
  | FetchLocalApplicationServersAction
  | FetchLocalApplicationServersCompletedAction
  | FetchLocalApplicationServersFailedAction
  | LocalApplicationServerCreatedAction
  | LocalApplicationServerUpdatedAction
  | FetchLocalApplicationChannelTypeAction
  | FetchLocalApplicationChannelTypeCompletedAction
  | FetchLocalApplicationChannelTypeFailedAction
  | LocalApplicationChannelTypeCreatedAction
  | LocalApplicationChannelTypeDeletedAction
  | FetchLocalApplicationEntryPointsAction
  | FetchLocalApplicationEntryPointsCompletedAction
  | FetchLocalApplicationEntryPointsFailedAction
  | LocalApplicationEntryPointCreatedAction
  | LocalApplicationEntryPointDeletedAction
  | FetchLocalApplicationOAuthClientAction
  | FetchLocalApplicationOAuthClientCompletedAction
  | FetchLocalApplicationOAuthClientFailedAction;

/*
 * Action creators.
 */

function localApplicationDeleteAction(localApplicationId: number): LocalApplicationDeleteAction {
  return {
    type: LocalApplicationActionsEnum.LOCAL_APPLICATION_DELETED,
    payload: {
      localApplicationId,
    },
  };
}

// Servers.

function fetchLocalApplicationServersAction(localApplicationId: number): FetchLocalApplicationServersAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS,
    payload: {
      localApplicationId,
    },
  };
}

function fetchLocalApplicationServersCompletedAction(
  localApplicationId: number,
  servers: ReadonlyArray<LocalApplicationServerModel>,
): FetchLocalApplicationServersCompletedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS_COMPLETED,
    payload: {
      localApplicationId,
      servers,
    },
  };
}

function fetchLocalApplicationServersFailedAction(
  localApplicationId: number,
): FetchLocalApplicationServersFailedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS_FAILED,
    payload: {
      localApplicationId,
    },
  };
}

function localApplicationServerCreatedAction(
  localApplicationId: number,
  server: LocalApplicationServerModel,
): LocalApplicationServerCreatedAction {
  return {
    type: LocalApplicationActionsEnum.LOCAL_APPLICATION_SERVER_CREATED,
    payload: {
      localApplicationId,
      server,
    },
  };
}

function localApplicationServerUpdatedAction(
  localApplicationId: number,
  server: LocalApplicationServerModel,
): LocalApplicationServerUpdatedAction {
  return {
    type: LocalApplicationActionsEnum.LOCAL_APPLICATION_SERVER_UPDATED,
    payload: {
      localApplicationId,
      server,
    },
  };
}

// Channel Type.

function fetchLocalApplicationChannelTypeAction(
  localApplicationId: number,
): FetchLocalApplicationChannelTypeAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE,
    payload: {
      localApplicationId,
    },
  };
}

function fetchLocalApplicationChannelTypeCompletedAction(
  localApplicationId: number,
  channelType: LocalApplicationChannelTypeModel,
): FetchLocalApplicationChannelTypeCompletedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_COMPLETED,
    payload: {
      localApplicationId,
      channelType,
    },
  };
}

function fetchLocalApplicationChannelTypeFailedAction(
  localApplicationId: number,
): FetchLocalApplicationChannelTypeFailedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_FAILED,
    payload: {
      localApplicationId,
    },
  };
}

function localApplicationChannelTypeCreatedAction(
  localApplicationId: number,
  channelType: LocalApplicationChannelTypeModel,
): LocalApplicationChannelTypeCreatedAction {
  return {
    type: LocalApplicationActionsEnum.LOCAL_APPLICATION_CHANNEL_TYPE_CREATED,
    payload: {
      localApplicationId,
      channelType,
    },
  };
}

function localApplicationChannelTypeDeletedAction(
  localApplicationId: number,
): LocalApplicationChannelTypeDeletedAction {
  return {
    type: LocalApplicationActionsEnum.LOCAL_APPLICATION_CHANNEL_TYPE_DELETED,
    payload: {
      localApplicationId,
    },
  };
}

// Entry Points.

function fetchLocalApplicationEntryPointsAction(
  localApplicationId: number,
): FetchLocalApplicationEntryPointsAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS,
    payload: {
      localApplicationId,
    },
  };
}

function fetchLocalApplicationEntryPointsCompletedAction(
  localApplicationId: number,
  entryPoints: ReadonlyArray<LocalApplicationEntryPointModel>,
): FetchLocalApplicationEntryPointsCompletedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS_COMPLETED,
    payload: {
      localApplicationId,
      entryPoints,
    },
  };
}

function fetchLocalApplicationEntryPointsFailedAction(
  localApplicationId: number,
): FetchLocalApplicationEntryPointsFailedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS_FAILED,
    payload: {
      localApplicationId,
    },
  };
}

function localApplicationEntryPointCreatedAction(
  localApplicationId: number,
  entryPoint: LocalApplicationEntryPointModel,
): LocalApplicationEntryPointCreatedAction {
  return {
    type: LocalApplicationActionsEnum.LOCAL_APPLICATION_ENTRY_POINT_CREATED,
    payload: {
      localApplicationId,
      entryPoint,
    },
  };
}

function localApplicationEntryPointDeletedAction(
  localApplicationId: number,
  entryPointId: number,
): LocalApplicationEntryPointDeletedAction {
  return {
    type: LocalApplicationActionsEnum.LOCAL_APPLICATION_ENTRY_POINT_DELETED,
    payload: {
      localApplicationId,
      entryPointId,
    },
  };
}

function fetchLocalApplicationOAuthClientAction(
  localApplicationId: number,
): FetchLocalApplicationOAuthClientAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT,
    payload: {
      localApplicationId,
    },
  };
}

function fetchLocalApplicationOAuthClientCompletedAction(
  localApplicationId: number,
  oAuthClient: OauthClientModel,
): FetchLocalApplicationOAuthClientCompletedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_COMPLETED,
    payload: {
      localApplicationId,
      oAuthClient,
    },
  };
}

function fetchLocalApplicationOAuthClientFailedAction(
  localApplicationId: number,
): FetchLocalApplicationOAuthClientFailedAction {
  return {
    type: LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_FAILED,
    payload: {
      localApplicationId,
    },
  };
}

/*
 * Action.
 */

export function fetchLocalApplicationsAsyncAction(options: {
  offset?: number;
  limit?: number;
  cancelToken?: CancelToken;
}): ThunkAsyncAction<SearchLocalApplicationsModel> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchLocalApplicationsUrl = addQueryToPath(`${companyUrl}/search_local_applications/`, {
      offset: options.offset ?? 0,
      limit: options.limit ?? 50,
    });

    try {
      const response = await fetchApiAsync(ApiSearchLocalApplications, fetchLocalApplicationsUrl, {
        cancelToken: options.cancelToken,
      });

      return response;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchLocalApplicationAsyncAction(
  localApplicationId: number,
): ThunkAsyncAction<LocalApplicationWithCreatorModel> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const response = await fetchApiAsync(
        ApiLocalApplicationWithCreator,
        `${companyUrl}/local_applications/${localApplicationId}`,
      );

      // If we have a channel type, we should try to fetch it when we load a single application.
      if (response.application.channelType) {
        dispatch(
          fetchLocalApplicationChannelTypeAsyncAction(
            response.application.id,
            response.application.channelType.id,
          ),
        );
      }

      dispatch(fetchLocalApplicationEntryPointsAsyncAction(response.application.id));
      return response;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchLocalApplicationsAppSecretAsyncAction(
  localApplicationId: number,
): ThunkAsyncAction<string> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const response = await fetchApiAsync(
        ApiLocalApplicationSigningSecret,
        `${companyUrl}/local_applications/${localApplicationId}/signing_secret`,
      );

      return response.secret;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function createLocalApplicationAsyncAction(
  localApplicationDetails: Pick<LocalApplicationModel, 'name' | 'description'>,
): ThunkAsyncAction<LocalApplicationModel | undefined> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const localApplication = await postApiAsync(
        ApiLocalApplication,
        `${companyUrl}/local_applications/`,
        localApplicationDetails,
      );
      return localApplication;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

export function deleteLocalApplicationAsyncAction(localApplicationId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const deleteAppUrl = `${companyUrl}/local_applications/${localApplicationId}`;

    try {
      await deleteAsync(deleteAppUrl);
      dispatch(localApplicationDeleteAction(localApplicationId));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

// Servers.

export function createLocalApplicationServerAsyncAction(
  localApplicationId: number,
  origin: string,
  config: LocalApplicationServerAuthenticationConfigModel,
): ThunkAsyncAction<LocalApplicationServerModel> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const localApplicationServer = await postApiAsync(
        ApiLocalApplicationServer,
        `${companyUrl}/local_applications/${localApplicationId}/application_servers`,
        {
          origin,
          authentication_config: serializeLocalApplicationServerAuthenticationConfig(config),
        },
      );

      dispatch(localApplicationServerCreatedAction(localApplicationId, localApplicationServer));
      return localApplicationServer;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function updateLocalApplicationServerAsyncAction(
  localApplicationId: number,
  serverId: number,
  origin: string,
  config: LocalApplicationServerAuthenticationConfigModel,
): ThunkAsyncAction<LocalApplicationServerModel> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const localApplicationServer = await putApiAsync(
        ApiLocalApplicationServer,
        `${companyUrl}/application_servers/${serverId}`,
        {
          origin,
          authentication_config: serializeLocalApplicationServerAuthenticationConfig(config),
        },
      );

      dispatch(localApplicationServerUpdatedAction(localApplicationId, localApplicationServer));
      return localApplicationServer;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchLocalApplicationServersAsyncAction(options: {
  localApplicationId: number;
  offset?: number;
  limit?: number;
}): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchLocalApplicationServersUrl = addQueryToPath(
      `${companyUrl}/local_applications/${options.localApplicationId}/search_application_servers/`,
      {
        offset: options.offset ?? 0,
        limit: options.limit ?? 50,
      },
    );
    dispatch(fetchLocalApplicationServersAction(options.localApplicationId));

    try {
      const servers = await fetchApiAsync(ApiLocalApplicationServers, fetchLocalApplicationServersUrl);
      dispatch(fetchLocalApplicationServersCompletedAction(options.localApplicationId, servers));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchLocalApplicationServersFailedAction(options.localApplicationId));
    }
  };
}

// Channel Type.

export function fetchLocalApplicationChannelTypeAsyncAction(
  localApplicationId: number,
  channelTypeId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchLocalApplicationChannelTypeUrl = `${companyUrl}/channel_types/${channelTypeId}`;
    dispatch(fetchLocalApplicationChannelTypeAction(localApplicationId));

    try {
      const channelType = await fetchApiAsync(
        ApiLocalApplicationChannelType,
        fetchLocalApplicationChannelTypeUrl,
      );
      dispatch(fetchLocalApplicationChannelTypeCompletedAction(localApplicationId, channelType));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchLocalApplicationChannelTypeFailedAction(localApplicationId));
    }
  };
}

export function createLocalApplicationChannelTypeAsyncAction(
  localApplicationId: number,
  options: {
    description: string;
    webhookUrl: string;
    settings: LocalApplicationChannelTypeSettingsModel;
  },
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const createChannelTypeUrl = `${companyUrl}/channel_types`;

    try {
      const channelType = await postApiAsync(ApiLocalApplicationChannelType, createChannelTypeUrl, {
        local_application_id: localApplicationId,
        description: options.description,
        webhook_url: options.webhookUrl,
        settings: {
          compose_mode: options.settings.composeMode,
          composer_type: options.settings.composerType,
          contact_type: options.settings.contactType,
          reply_mode: options.settings.replyMode,
          supported_attachment_types: options.settings.supportedAttachmentTypes,
          supports_imported_message_notifications: options.settings.supportsImportedMessageNotifications,
        },
      });
      dispatch(localApplicationChannelTypeCreatedAction(localApplicationId, channelType));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function updateLocalApplicationChannelTypeAsyncAction(
  localApplicationId: number,
  channelTypeId: number,
  options: {
    description: string;
    webhookUrl: string;
    settings: Omit<LocalApplicationChannelTypeSettingsModel, 'contactType'>;
  },
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const updateChannelTypeUrl = `${companyUrl}/channel_types/${channelTypeId}`;

    try {
      const channelType = await putApiAsync(ApiLocalApplicationChannelType, updateChannelTypeUrl, {
        description: options.description,
        webhook_url: options.webhookUrl,
        settings: {
          compose_mode: options.settings.composeMode,
          composer_type: options.settings.composerType,
          reply_mode: options.settings.replyMode,
          supported_attachment_types: options.settings.supportedAttachmentTypes,
          supports_imported_message_notifications: options.settings.supportsImportedMessageNotifications,
        },
      });
      dispatch(fetchLocalApplicationChannelTypeCompletedAction(localApplicationId, channelType));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteLocalApplicationChannelTypeAsyncAction(
  localApplicationId: number,
  channelTypeId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const deleteChannelTypeUrl = `${companyUrl}/channel_types/${channelTypeId}`;

    try {
      await deleteAsync(deleteChannelTypeUrl);
      dispatch(localApplicationChannelTypeDeletedAction(localApplicationId));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

// Entry Points.

export function fetchLocalApplicationEntryPointsAsyncAction(localApplicationId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchLocalApplicationEntryPointsUrl = `${companyUrl}/local_applications/${localApplicationId}/application_entry_points/`;
    dispatch(fetchLocalApplicationEntryPointsAction(localApplicationId));

    try {
      const entryPoints = await fetchApiAsync(
        ApiLocalApplicationEntryPoints,
        fetchLocalApplicationEntryPointsUrl,
      );
      dispatch(fetchLocalApplicationEntryPointsCompletedAction(localApplicationId, entryPoints));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchLocalApplicationEntryPointsFailedAction(localApplicationId));
    }
  };
}

export function createLocalApplicationEntryPointAsyncAction(
  localApplicationId: number,
  entryPointType: ApplicationEntryPointTypesEnum,
  options: ApplicationEntryPointActionModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const createEntryPointUrl = `${companyUrl}/application_entry_points`;

    try {
      const entryPoint = await postApiAsync(ApiLocalApplicationEntryPoint, createEntryPointUrl, {
        local_application_id: localApplicationId,
        type: entryPointType,
        action: {
          endpoint: options.endpoint,
          features: options.features,
          type: options.type,
          sdk_version: options.sdkVersion,
        },
      });
      dispatch(localApplicationEntryPointCreatedAction(localApplicationId, entryPoint));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function updateLocalApplicationEntryPointAsyncAction(
  localApplicationId: number,
  entryPointId: number,
  entryPointType: ApplicationEntryPointTypesEnum,
  options: ApplicationEntryPointActionModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const updateEntrypointUrl = `${companyUrl}/application_entry_points/${entryPointId}`;

    try {
      const entryPoint = await putApiAsync(ApiLocalApplicationEntryPoint, updateEntrypointUrl, {
        local_application_id: localApplicationId,
        type: entryPointType,
        action: {
          endpoint: options.endpoint,
          features: options.features,
          type: options.type,
          sdk_version: options.sdkVersion,
        },
      });
      dispatch(fetchLocalApplicationEntryPointsCompletedAction(localApplicationId, [entryPoint]));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteLocalApplicationEntryPointAsyncAction(
  localApplicationId: number,
  entryPointId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const deleteEntryPointUrl = `${companyUrl}/application_entry_points/${entryPointId}`;

    try {
      await deleteAsync(deleteEntryPointUrl);
      dispatch(localApplicationEntryPointDeletedAction(localApplicationId, entryPointId));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchLocalApplicationOAuthClientAsyncAction(localApplicationId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchOAuthClientUrl = `${companyUrl}/local_applications/${localApplicationId}/oauth_client/`;
    dispatch(fetchLocalApplicationOAuthClientAction(localApplicationId));

    try {
      const oAuthClient = await fetchApiAsync(ApiOauthClient, fetchOAuthClientUrl);
      dispatch(fetchLocalApplicationOAuthClientCompletedAction(localApplicationId, oAuthClient));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchLocalApplicationOAuthClientFailedAction(localApplicationId));
    }
  };
}

export function fetchLocalApplicationsOAuthClientSecretAsyncAction(
  localApplicationId: number,
): ThunkAsyncAction<string> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const response = await fetchApiAsync(
        ApiOauthClientSecret,
        `${companyUrl}/local_applications/${localApplicationId}/oauth_client/client_secret`,
      );

      return response.clientSecret;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}
